// Serviceability service for water and address checks

import { api } from '@/api/client';
import type { AddressData, WaterServiceData, WaterPlan } from '@/types';
import { normalizeSchedule, type ScheduleSource } from '@/utils/scheduling';
import { logPlanScheduleDiagnostics } from '@/utils/scheduleLogging';
import { logger } from '@/utils/logger';

function normalizeWaterPlan(plan: WaterPlan & ScheduleSource): WaterPlan {
  return {
    ...plan,
    ...normalizeSchedule(plan),
  };
}

export interface ServiceabilityResult {
  success: boolean;
  waterAvailable: boolean;
  serviceData?: WaterServiceData;
  error?: string;
}

export async function checkWaterServiceability(
  addressData: AddressData,
  userId?: string,
  isBusiness?: boolean,
  isRental?: boolean
): Promise<ServiceabilityResult> {
  try {
    // Check water serviceability using the Water/plans endpoint
    // This only requires zip code, not personal information
    if (!addressData.zip) {
      logger.warn({ area: 'serviceability' }, 'checkWaterServiceability: No zip code provided');
      return {
        success: false,
        waterAvailable: false,
        error: 'Zip code required for serviceability check',
      };
    }

    const params = new URLSearchParams({
      serviceName: 'Water',
      zipCode: addressData.zip,
    });
    if (isBusiness !== undefined) params.append('isBusiness', String(isBusiness));
    if (isRental !== undefined) params.append('isRental', String(isRental));

    const response = await api.get<{
      plans?: WaterPlan[];
      success?: boolean;
      error?: string;
    }>(
      `/api/2turniton/services/Water/plans?${params}`,
      userId ? { userId } : {}
    );

    logger.info({ area: 'serviceability' }, 'Water serviceability response received');

    if (response.success && response.data) {
      // API returns plans directly in data array, not nested under data.plans
      const plans: WaterPlan[] = Array.isArray(response.data)
        ? response.data
        : (response.data.plans || []);
      logPlanScheduleDiagnostics({
        area: 'serviceability',
        serviceName: 'Water',
        stage: 'serviceability-response',
        zipCode: addressData.zip,
        userId,
        plans,
      });
      const normalizedPlans = plans.map(normalizeWaterPlan);
      const waterAvailable = normalizedPlans.length > 0;

      logger.info({ area: 'serviceability' }, `Water plans found: ${plans.length}, waterAvailable: ${waterAvailable}`);

      // Build service data from plans
      const firstPlan = normalizedPlans[0];
      return {
        success: true,
        waterAvailable,
        serviceData: waterAvailable ? {
          isAvailable: true,
          name: firstPlan?.name || 'Water Service',
          vendorName: firstPlan?.vendorName || 'Water Provider',
          description: firstPlan?.shortDescription,
          logo: firstPlan?.logo,
          planData: normalizedPlans,
        } : undefined,
      };
    }

    // API returned an error or no data
    return {
      success: false,
      waterAvailable: false,
      error: response.error || 'Failed to check water serviceability',
    };
  } catch (error) {
    logger.error({ area: 'serviceability' }, 'Serviceability check error', error instanceof Error ? error : undefined);
    return {
      success: false,
      waterAvailable: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export async function getWaterPlans(
  addressData: AddressData,
  userId: string
): Promise<WaterPlan[]> {
  const params = new URLSearchParams({
    addressLine1: addressData.fullAddress,
    city: addressData.city,
    state: addressData.stateCode,
    zip: addressData.zip,
  });

  const response = await api.get<{ plans: WaterPlan[] }>(
    `/api/2turniton/services/Water/plans?${params}`,
    { userId }
  );

  if (response.success && response.data) {
    const plans = response.data.plans || [];
    logPlanScheduleDiagnostics({
      area: 'serviceability',
      serviceName: 'Water',
      stage: 'get-water-plans-response',
      zipCode: addressData.zip,
      userId,
      plans,
    });
    return plans.map(normalizeWaterPlan);
  }

  return [];
}

export async function fetchOnitWaterFiltrationPlan(
  zipCode: string,
  userId?: string,
  isBusiness?: boolean,
  isRental?: boolean
): Promise<WaterPlan | null> {
  const params = new URLSearchParams({
    serviceName: 'Water Filtration',
    zipCode,
  });
  if (isBusiness !== undefined) params.append('isBusiness', String(isBusiness));
  if (isRental !== undefined) params.append('isRental', String(isRental));

  const response = await api.get<WaterPlan[]>(
    `/api/2turniton/services/Water Filtration/plans?${params}`,
    userId ? { userId } : {}
  );

  if (response.success && response.data) {
    const plans = Array.isArray(response.data) ? response.data : [];
    logPlanScheduleDiagnostics({
      area: 'serviceability',
      serviceName: 'Water Filtration',
      stage: 'onit-response',
      zipCode,
      userId,
      plans,
    });
    if (plans.length === 0) {
      logger.info({ area: 'serviceability' }, 'ONIT Water Filtration not available at this zip');
    }
    return plans[0] ? normalizeWaterPlan(plans[0]) : null;
  }
  logger.info({ area: 'serviceability' }, 'ONIT Water Filtration not serviceable (API returned no data)');
  return null;
}

export async function getESIID(addressData: AddressData): Promise<string | null> {
  // Strip street type suffix for better ESIID lookup
  const stripStreetType = (streetName: string): string => {
    if (!streetName) return streetName;
    const suffixes = [
      'Street', 'St\\.?', 'Avenue', 'Ave\\.?', 'Boulevard', 'Blvd\\.?',
      'Drive', 'Dr\\.?', 'Road', 'Rd\\.?', 'Lane', 'Ln\\.?',
      'Court', 'Ct\\.?', 'Circle', 'Cir\\.?', 'Place', 'Pl\\.?',
      'Way', 'Parkway', 'Pkwy\\.?', 'Trail', 'Trl\\.?', 'Terrace', 'Ter\\.?',
    ];
    const regex = new RegExp(`\\s+(${suffixes.join('|')})$`, 'i');
    return streetName.replace(regex, '').trim();
  };

  const params = new URLSearchParams();
  if (addressData.streetNumber) params.append('streetNumber', addressData.streetNumber);
  if (addressData.route) params.append('streetName', stripStreetType(addressData.route));
  if (addressData.unit) params.append('apartment', addressData.unit);
  if (addressData.city) params.append('city', addressData.city);
  if (addressData.stateCode) params.append('state', addressData.stateCode);
  if (addressData.zip) params.append('zipCode', addressData.zip);

  const response = await api.get<{ results: Array<{ ESIID?: string; esiid?: string }> }>(
    `/api/2turniton/users/get-esiid?${params}`
  );

  if (response.success && response.data?.results?.length) {
    const firstResult = response.data.results[0];
    return firstResult.ESIID || firstResult.esiid || null;
  }

  return null;
}
